export const USER_ROLES = Object.freeze({
  ADMIN: "admin",
  TEACHER: "teacher",
  RESEARCHER: "researcher",
  STUDENT: "student",
});

export const APP_ROUTE_NAMES = Object.freeze({
  HOME: "home",
  ADMIN: "admin",
  TEACHER: "teacher",
  RESEARCHER: "researcher",
  TEACHER_GROUPS: "teacher-groups",
  TEACHER_GROUP_DETAIL: "teacher-group-detail",
  RESEARCHER_GROUPS: "researcher-groups",
  RESEARCHER_GROUP_DETAIL: "researcher-group-detail",
  GAME_REPLAY: "game-replay",
});

export const PERMISSIONS = Object.freeze({
  VIEW_USERS: "view_users",
  VIEW_ADMIN_USERS: "view_admin_users",
  VIEW_ALL_GAMES: "view_all_games",
  VIEW_OWN_GAMES: "view_own_games",
  VIEW_PUBLIC_GAMES: "view_public_games",
  VIEW_SHARED_GAMES: "view_shared_games",
  MANAGE_TEACHER_APPLICATIONS: "manage_teacher_applications",
  MANAGE_USER_ROLES: "manage_user_roles",
  MANAGE_SHARING_GROUPS: "manage_sharing_groups",
  SHARE_GAMES: "share_games",
  GENERATE_GAME_REPORTS: "generate_game_reports",
  DOWNLOAD_TRANSCRIPTS: "download_transcripts",
  MANAGE_ALL_GAME_VISIBILITY: "manage_all_game_visibility",
  MANAGE_OWN_GAME_VISIBILITY: "manage_own_game_visibility",
});

export const ROLE_PERMISSIONS = Object.freeze({
  [USER_ROLES.ADMIN]: Object.freeze([
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_ADMIN_USERS,
    PERMISSIONS.VIEW_ALL_GAMES,
    PERMISSIONS.VIEW_PUBLIC_GAMES,
    PERMISSIONS.VIEW_SHARED_GAMES,
    PERMISSIONS.MANAGE_TEACHER_APPLICATIONS,
    PERMISSIONS.MANAGE_USER_ROLES,
    PERMISSIONS.MANAGE_SHARING_GROUPS,
    PERMISSIONS.SHARE_GAMES,
    PERMISSIONS.GENERATE_GAME_REPORTS,
    PERMISSIONS.DOWNLOAD_TRANSCRIPTS,
    PERMISSIONS.MANAGE_ALL_GAME_VISIBILITY,
  ]),
  [USER_ROLES.RESEARCHER]: Object.freeze([
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_PUBLIC_GAMES,
    PERMISSIONS.VIEW_SHARED_GAMES,
    PERMISSIONS.MANAGE_SHARING_GROUPS,
    PERMISSIONS.SHARE_GAMES,
    PERMISSIONS.GENERATE_GAME_REPORTS,
    PERMISSIONS.DOWNLOAD_TRANSCRIPTS,
  ]),
  [USER_ROLES.TEACHER]: Object.freeze([
    PERMISSIONS.VIEW_OWN_GAMES,
    PERMISSIONS.VIEW_PUBLIC_GAMES,
    PERMISSIONS.VIEW_SHARED_GAMES,
    PERMISSIONS.MANAGE_SHARING_GROUPS,
    PERMISSIONS.SHARE_GAMES,
    PERMISSIONS.GENERATE_GAME_REPORTS,
    PERMISSIONS.DOWNLOAD_TRANSCRIPTS,
    PERMISSIONS.MANAGE_OWN_GAME_VISIBILITY,
  ]),
  [USER_ROLES.STUDENT]: Object.freeze([]),
});

export const ROLE_INHERITANCE = Object.freeze({
  [USER_ROLES.ADMIN]: Object.freeze([USER_ROLES.RESEARCHER, USER_ROLES.TEACHER, USER_ROLES.STUDENT]),
  [USER_ROLES.RESEARCHER]: Object.freeze([USER_ROLES.TEACHER, USER_ROLES.STUDENT]),
  [USER_ROLES.TEACHER]: Object.freeze([USER_ROLES.STUDENT]),
  [USER_ROLES.STUDENT]: Object.freeze([]),
});

const ROLE_PRIORITY = Object.freeze([
  USER_ROLES.ADMIN,
  USER_ROLES.RESEARCHER,
  USER_ROLES.TEACHER,
  USER_ROLES.STUDENT,
]);

const DASHBOARD_ROLE_LABEL_KEYS = Object.freeze({
  [USER_ROLES.ADMIN]: "role-admin",
  [USER_ROLES.RESEARCHER]: "role-researcher",
  [USER_ROLES.TEACHER]: "role-teacher",
});

const DASHBOARD_ROUTE_NAMES = Object.freeze({
  [USER_ROLES.ADMIN]: APP_ROUTE_NAMES.ADMIN,
  [USER_ROLES.RESEARCHER]: APP_ROUTE_NAMES.RESEARCHER,
  [USER_ROLES.TEACHER]: APP_ROUTE_NAMES.TEACHER,
});

export function isAdminUser(user) {
  if (!user) return false;
  const value = user.isAdmin;
  return value === 1 || value === "1" || value === true;
}

export function isTeacherUser(user) {
  if (!user) return false;
  const value = user.isTeacher;
  return value === 1 || value === "1" || value === true;
}

export function isResearcherUser(user) {
  if (!user) return false;
  const value = user.isResearcher;
  return value === 1 || value === "1" || value === true;
}

export function getDirectUserRoles(user) {
  const roles = [];

  if (isAdminUser(user)) roles.push(USER_ROLES.ADMIN);
  if (isResearcherUser(user)) roles.push(USER_ROLES.RESEARCHER);
  if (isTeacherUser(user)) roles.push(USER_ROLES.TEACHER);

  return roles.length ? roles : [USER_ROLES.STUDENT];
}

export function expandUserRoles(roles = []) {
  const expanded = new Set();
  const queue = [...roles];

  while (queue.length) {
    const role = queue.shift();
    if (!role || expanded.has(role)) continue;

    expanded.add(role);
    const inheritedRoles = ROLE_INHERITANCE[role] || [];
    inheritedRoles.forEach((inheritedRole) => {
      if (!expanded.has(inheritedRole)) queue.push(inheritedRole);
    });
  }

  if (expanded.size === 0) expanded.add(USER_ROLES.STUDENT);

  return ROLE_PRIORITY.filter((role) => expanded.has(role));
}

export function getEffectiveUserRoles(user) {
  return expandUserRoles(getDirectUserRoles(user));
}

export function getPrimaryUserRole(user) {
  return getDirectUserRoles(user)[0] || USER_ROLES.STUDENT;
}

export function canAssumeRole(user, role) {
  if (!role) return false;
  return getDashboardRoles(user).includes(role);
}

export function getRoleContext(user, preferredRole = null) {
  if (canAssumeRole(user, preferredRole)) return preferredRole;
  return getPrimaryUserRole(user);
}

export function getRolePermissions(role) {
  return ROLE_PERMISSIONS[role] || [];
}

export function getRoleContextPermissions(role) {
  return expandUserRoles([role]).flatMap((currentRole) => getRolePermissions(currentRole));
}

export function userHasPermission(user, permission, roleContext = null) {
  if (!permission) return true;
  const resolvedRole = getRoleContext(user, roleContext);
  return getRoleContextPermissions(resolvedRole).includes(permission);
}

export function userHasAnyPermission(user, permissions = [], roleContext = null) {
  if (!Array.isArray(permissions) || permissions.length === 0) return true;
  return permissions.some((permission) => userHasPermission(user, permission, roleContext));
}

export function userHasAnyRole(user, allowedRoles = []) {
  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true;
  const effectiveRoles = new Set(getEffectiveUserRoles(user));
  return allowedRoles.some((role) => effectiveRoles.has(role));
}

export function getDefaultRoleRoute(user) {
  const role = getPrimaryUserRole(user);

  if (role === USER_ROLES.ADMIN) return { name: APP_ROUTE_NAMES.ADMIN };
  if (role === USER_ROLES.RESEARCHER) return { name: APP_ROUTE_NAMES.RESEARCHER };
  if (role === USER_ROLES.TEACHER) return { name: APP_ROUTE_NAMES.HOME };

  return { name: APP_ROUTE_NAMES.HOME };
}

export function getPostAuthLandingRoute(user, activeRole = null) {
  const roleContext = getRoleContext(user, activeRole);
  if (roleContext === USER_ROLES.ADMIN) return { name: APP_ROUTE_NAMES.ADMIN };
  if (roleContext === USER_ROLES.RESEARCHER) return { name: APP_ROUTE_NAMES.RESEARCHER };
  return null;
}

export function getDashboardRoles(user) {
  return getEffectiveUserRoles(user).filter((role) => Boolean(DASHBOARD_ROUTE_NAMES[role]));
}

export function getRoleSelectorOptions(user) {
  return getDashboardRoles(user).map((role) => ({
    value: role,
    labelKey: DASHBOARD_ROLE_LABEL_KEYS[role] || null,
    label: role,
  }));
}

export function getRoleRoute(role) {
  if (role === USER_ROLES.ADMIN) return { name: APP_ROUTE_NAMES.ADMIN };
  if (role === USER_ROLES.RESEARCHER) return { name: APP_ROUTE_NAMES.RESEARCHER };
  return { name: APP_ROUTE_NAMES.HOME };
}
